import { AgGridSolidRef } from '@ag-grid-community/solid';
import { Box, IconButton, Stack, Typography } from '@atgfr8/atg-ui-tailwind';
import {
  ArrowDownCircleIcon,
  ArrowUpCircleIcon,
  CloseIcon,
} from '@atgfr8/icons';
import { BasicModal, Button, RadioGroup, ToastType } from '@components';
import { deleteClaimedCustomerApi } from '@store/dormantPool';
import { dormatPoolStore, setDormantPoolStore } from '@store/dormantPool/store';
import { closeModal, openModal } from '@store/modals';
import { DormantPoolListType } from '@typeDefinitions/dormantPoolTypes';
import { ConfigManager } from '@utils/ConfigManager';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { Accessor, createSignal, Show } from 'solid-js';

type ClaimedCustomersPropsType = {
  viewMode: Accessor<'List' | 'Grid'>;
  onCustomerRemovedFromGrid?: () => void;
  gridRef?: Accessor<AgGridSolidRef | undefined>;
};

export const ClaimedCustomers = (props: ClaimedCustomersPropsType) => {
  const [showSelectedCustomers, setShowSelectedCustomers] =
    createSignal<boolean>(false);

  const [customerToDelete, setCustomerToDelete] =
    createSignal<DormantPoolListType | null>(null);

  const [selectedReason, setSelectedReason] = createSignal<string>('');

  const [customerDeleteLoading, setCustomerDeleteLoading] =
    createSignal<boolean>(false);

  const handleDeleteClaimedCustomer = async () => {
    const customer = customerToDelete();

    if (customer) {
      setCustomerDeleteLoading(true);
      const response = await deleteClaimedCustomerApi({
        id: customer.id,
        comment: selectedReason(),
      });

      if (Boolean(response)) {
        const updatedClaimedList = dormatPoolStore.claimed.filter(
          (item) => item.id !== customer.id,
        );
        if (updatedClaimedList.length === 0) {
          setShowSelectedCustomers(false);
        }
        setDormantPoolStore('claimed', updatedClaimedList);
        setSelectedReason('');
        setCustomerToDelete(null);
        setCustomerDeleteLoading(false);
        closeModal('delete_claimed_customer');
        handleToast(ToastType.Success, 'Customer returned to the pool');

        if (props.viewMode() === 'List') {
          props.gridRef?.()?.api.refreshServerSide({ purge: true });
        } else {
          props.onCustomerRemovedFromGrid && props.onCustomerRemovedFromGrid();
        }
      }
    }
  };

  return (
    <Box class="flex flex-col" gap={1}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {props.viewMode() == 'List' &&
          (showSelectedCustomers() ? (
            <ArrowDownCircleIcon
              size={32}
              class="text-[#000000]! cursor-pointer"
              onClick={() => setShowSelectedCustomers(false)}
            />
          ) : (
            <ArrowUpCircleIcon
              size={32}
              class="text-[#000000]! cursor-pointer"
              onClick={() => setShowSelectedCustomers(true)}
            />
          ))}
        {props.viewMode() == 'List' ? (
          <Typography variant="body2" class="text-lg!">
            Selected {dormatPoolStore.claimed.length || 0} of your 5 available
            customers
          </Typography>
        ) : (
          <Box class="border w-full! border-gray-300 px-4 py-3 bg-[#f5f5f5] text-base min-h-[56px] block! text-[20px]!">
            Selected
            <strong class="mx-1">{dormatPoolStore.claimed.length || 0}</strong>
            of your<strong class="mx-1">5</strong>available customers
          </Box>
        )}
      </Stack>
      <Show when={props.viewMode() == 'List' ? showSelectedCustomers() : true}>
        <Box
          class="flex flex-wrap"
          style={{
            'flex-direction': props.viewMode() == 'List' ? 'row' : 'column',
          }}
          gap={1}
        >
          {dormatPoolStore.claimed.map((item) => {
            return (
              <Box
                class="flex items-center border border-gray-300 px-2 py-1 bg-white cursor-pointer min-w-[200px] justify-between "
                onClick={() => {
                  const customerId = item.id;
                  if (customerId) {
                    window.open(
                      `${ConfigManager.domainUrl}/customer/details/${customerId}`,
                      '_blank',
                    );
                  }
                }}
              >
                <Box class="flex items-center gap-2">
                  <Typography variant="body2" class="text-[16px]!">
                    {item.customerName}
                  </Typography>
                </Box>
                <IconButton
                  size="sm"
                  dataTestId="claimed-customers-remove-button"
                  on:click={(e) => {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                    e.stopPropagation();
                    setCustomerToDelete(item);
                    openModal('delete_claimed_customer');
                  }}
                >
                  <CloseIcon class="text-[18px]!" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      </Show>
      <BasicModal
        id="delete_claimed_customer"
        title="Why didn't this dormant account work for you?"
        footer={false}
        width="40vw"
        onClose={() => {
          closeModal('delete_claimed_customer');
          setSelectedReason('');
          setCustomerToDelete(null);
        }}
      >
        <Stack spacing={2}>
          <RadioGroup
            direction="vertical"
            value={selectedReason()}
            onChange={setSelectedReason}
            options={dormatPoolStore.ATGData.customer.dormantCustomerReturnComments.map(
              (item) => ({ label: item, value: item }),
            )}
          />

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              label="Remove Customer"
              onClick={handleDeleteClaimedCustomer}
              disabled={customerDeleteLoading() || !selectedReason()}
              isLoading={customerDeleteLoading()}
              dataTestId={formatDataTestId('Remove Customer', 'button')}
            />
            <Button
              label="Cancel"
              variant="outlined"
              onClick={() => {
                setSelectedReason('');
                setCustomerToDelete(null);
                closeModal('delete_claimed_customer');
              }}
              dataTestId={formatDataTestId('Cancel', 'button')}
            />
          </Box>
        </Stack>
      </BasicModal>
    </Box>
  );
};
